import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import get from 'lodash/get'

import { Loading, ErrorView } from '@fielded/shared-ui'
import { toast } from '@fielded/shared-ui/src/components/Page/Page'
import { getStatesByCountry } from '@fielded/fs-api/lib/service/tools/territory-lookup'
import capitalizeAllWords from '@fielded/shared-ui/src/utils/capitalizeAll'

import { withUser } from '../../../common/AuthenticationProvider'
import { withApi } from '../../../common/ApiProvider'
import CarrierManagementAddEdit from '../components/CarrierManagementAddEdit'

const CarrierManagementAddEditContainer = ({
  config,
  api,
  user,
  match
}) => {
  const { carrierId } = match.params
  const history = useHistory()
  const formRef = useRef()
  const emptyServiceArea = {
    state: '',
    priority: 'primary'
  }
  const isCreate = carrierId === 'new'
  const isCarrierUser = user.roles.includes('feature:userRole:psm-carrier')
  const stateOptions = getStatesByCountry(get(user, 'location.country'))
    .map(state => {
      return {
        value: state,
        label: capitalizeAllWords(state.split(':state:')[1])
      }
    })

  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [serviceAreas, setServiceAreas] = useState([emptyServiceArea])
  const [carrierRecord, setCarrierRecord] = useState()
  const [carrierState, setCarrierState] = useState()
  const [carrierFleets, setCarrierFleets] = useState([])
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false)
  const [attemptedServiceAreaIndices, setAttemptedServiceAreaIndices] = useState(new Set())

  const initialize = async () => {
    if (!isCreate) {
      try {
        const carrierOrganisation = await api.carrier.get(carrierId)
        const carrier = get(carrierOrganisation, 'organisation')
        const serviceArears = carrierOrganisation.operationalStates.map((opState) => ({
          state: opState.state,
          priority: opState.priority
        }))
        const fleetCategories = carrierOrganisation.fleetCategories.map((fleet) => fleet.category)
        setCarrierRecord(carrier)
        setServiceAreas(serviceArears)
        setCarrierState(carrier.state)
        setCarrierFleets(fleetCategories)
      } catch (e) {
        setError(e)
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    initialize()
  }, [])

  const onAddServiceArea = () => {
    const updatedServiceAreas = [...serviceAreas, emptyServiceArea]
    setServiceAreas(updatedServiceAreas)
  }

  const onRemoveServiceArea = (index) => {
    let updatedServiceAreas = serviceAreas.slice()
    updatedServiceAreas.splice(index, 1)
    setServiceAreas(updatedServiceAreas)
  }

  const onHandleServiceAreaChange = (index, value, property) => {
    let updatedServiceAreas = [...serviceAreas]
    updatedServiceAreas[index][property] = value
    setServiceAreas(updatedServiceAreas)
  }

  const onHandleStateChange = (value) => {
    setCarrierState(value)
  }

  const onSave = async () => {
    setSaving(true)

    setHasSubmitAttempted(true)

    // Track which service areas have been part of a form submission attempt.
    const attemptedIndices = new Set(Array.from({ length: serviceAreas.length }, (_, index) => index))
    setAttemptedServiceAreaIndices(attemptedIndices)

    const formValidationErrors = await formRef.current.validate()

    if (formValidationErrors) {
      setSaving(false)
      window.scrollTo(0, 0)
      return
    }

    const fields = formRef.current.getFields()

    const carrierData = {
      organisation: {
        ...(isCreate ? {} : { id: carrierId }),
        name: fields.name,
        phone_number: fields.phone,
        email_addresses: [fields.email],
        country: get(user, 'location.country'),
        state: carrierState,
        lga: fields.lga,
        city: fields.city,
        street: fields.street,
        type: 'carrier',
        category: 'external',
        status: 'active'
      },
      fleetCategories: fields.fleet.map((carrierFleet) => ({ category: carrierFleet })),
      operationalStates: serviceAreas
    }

    try {
      if (isCreate) {
        await api.carrier.create(carrierData)
      } else {
        await api.carrier.update(carrierId, carrierData)
      }

      if (!isCreate) {
        setSaving(false)
        toast({
          title: 'Carrier updated',
          type: 'success',
          children: `Carrier "${fields.name}" has been updated successfully`
        })
      }
      history.push({
        pathname: '/settings/carriers',
        state: isCreate ? { carrierName: fields.name } : {}
      })
    } catch (error) {
      setError(error)
      setSaving(false)
    }
  }

  if (error) {
    return <ErrorView error={error} />
  }

  if (loading) {
    return <Loading />
  }

  return (
    <CarrierManagementAddEdit
      carrierRecord={carrierRecord}
      carrierState={carrierState}
      carrierFleets={carrierFleets}
      history={history}
      serviceAreas={serviceAreas}
      stateOptions={stateOptions}
      onHandleServiceAreaChange={onHandleServiceAreaChange}
      onAddServiceArea={onAddServiceArea}
      onRemoveServiceArea={onRemoveServiceArea}
      onHandleStateChange={onHandleStateChange}
      onSave={onSave}
      saving={saving}
      formRef={formRef}
      isEdit={!isCreate}
      viewOnly={isCarrierUser}
      hasSubmitAttempted={hasSubmitAttempted}
      attemptedServiceAreaIndices={attemptedServiceAreaIndices}
    />
  )
}

export default withApi(withUser(CarrierManagementAddEditContainer))
